import { rem } from 'polished'
import styled, { keyframes } from 'styled-components'
import clsx from 'clsx'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useFetchCartPanelOffers } from '../hooks/useFetchCartPanelOffers'
import { useCartPanel } from '../hooks/useCartPanel'
import CartPanel from '../components/CartPanel'

const slideInFromInlineEnd = keyframes`
  from { transform: translateX(var(--cart-panel-inline-end-offset)); }
  to { transform: translateX(0); }
`

const slideOutToInlineEnd = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(var(--cart-panel-inline-end-offset)); }
`

const CartPanelWrapper = styled.div`
  --cart-panel-inline-end-offset: 100%;
  --cart-panel-shadow-x: ${rem(-4)};

  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  animation-duration: ${MODAL_TRANSITION_DURATION}ms;
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;
  animation-name: ${slideInFromInlineEnd};
  box-shadow: var(--cart-panel-shadow-x) 0 ${rem(24)} rgba(15, 23, 42, 0.15);

  [dir="rtl"] & {
    --cart-panel-inline-end-offset: -100%;
    --cart-panel-shadow-x: ${rem(4)};
  }

  &.closing {
    animation-name: ${slideOutToInlineEnd};
  }
`

interface Props {
  isClosing: boolean
  onClose: () => void
}

function CartPanelContainer({ isClosing, onClose }: Props) {
  const { chatId, isStreaming, isStreamOpen } = useChatStreamStatus()
  const {
    itineraryData,
    multiCartId,
    deleteManualItineraryTarget,
    isManualItineraryDeletePending,
    isFlightCartMutationPending,
  } = useItineraryContext()
  const agentActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending || isFlightCartMutationPending
  useFetchCartPanelOffers(itineraryData)
  const { sendMessage } = useChatActions()
  const isLoggedIn = useAppSelector(selectLoggedIn)

  const {
    items,
    isLoading,
    totalPrice,
    totalValue,
    totalItemCount,
    hasCheckoutReadyItems,
    showReadyItemsOnlyNote,
    loyaltyCalculationRequests,
    handleToggleInclude,
    handleSetDates,
    handleChooseTickets,
    handleChooseDate,
    handleChooseTime,
    handleChooseDifferentTickets,
    handleChooseFlight,
    handleRefreshFlight,
    handleChangeFlight,
    handleViewFlightDetails,
    refreshingFlightKey,
    handleDelete,
    handleCheckout,
    isCheckingOut,
    flightSearchDrawer,
  } = useCartPanel({
    multiCartId,
    itineraryData,
    chatId,
    isStreaming: agentActionsDisabled,
    sendMessage,
    deleteManualItineraryTarget,
    isLoggedIn,
  })

  return (
    <CartPanelWrapper className={clsx({ closing: isClosing })}>
      <CartPanel
        items={items}
        isLoading={isLoading}
        totalPrice={totalPrice}
        totalValue={totalValue}
        totalItemCount={totalItemCount}
        hasCheckoutReadyItems={hasCheckoutReadyItems}
        showReadyItemsOnlyNote={showReadyItemsOnlyNote}
        loyaltyCalculationRequests={loyaltyCalculationRequests}
        isLoggedIn={isLoggedIn}
        isStreaming={agentActionsDisabled}
        onClose={onClose}
        onToggleInclude={handleToggleInclude}
        onSetDates={handleSetDates}
        onChooseTickets={handleChooseTickets}
        onChooseDate={handleChooseDate}
        onChooseTime={handleChooseTime}
        onChooseDifferentTickets={handleChooseDifferentTickets}
        onChooseFlight={handleChooseFlight}
        onRefreshFlight={handleRefreshFlight}
        onChangeFlight={handleChangeFlight}
        onViewFlightDetails={handleViewFlightDetails}
        refreshingFlightKey={refreshingFlightKey}
        onDelete={handleDelete}
        onCheckout={handleCheckout}
        isCheckoutLoading={isCheckingOut}
        flightSearchDrawer={flightSearchDrawer}
      />
    </CartPanelWrapper>
  )
}

export default CartPanelContainer
